'use client';

import {
  AccessTokenType,
  ActionType,
  InAppEvent,
  InAppEventName,
  InAppEventParams,
  InAppEventType,
} from '@diritto/common/app/inapp-event.constant';
import { UsedOutsideProviderError } from '@diritto/common/utils/exceptions';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { EventHandler, EventListener as EventHandlerListener } from 'libs/EventHandler';
import { useSsoLink } from 'state-hooks/accounts/useSsoLink';
import { AuthErrorCodes } from '@diritto/common/account/constants/auth.error-codes.constants';
import { SsoType } from '@diritto/common/account/constants/sso.constant';
import { buildUrl } from '@diritto/common/utils/urls';
import axios, { AxiosError } from 'axios';
import { DirittoAppBridge } from 'libs/DirittoAppBridge';
import { useRouter } from 'next/navigation';
import { useSignInMutation } from 'queries/account/useSignInMutation';
import { showAlertModal } from 'utils/modal';
import { SentryService } from 'utils/SentryService';
import { buildSignInUrl } from 'utils/url';
import { parseUserAgent, UseClientType } from '@diritto/common/utils/userAgent';

interface InAppState {
  isPushOn: boolean;
  userAgent: string | undefined;
  addActionEventListener: (listener: EventHandlerListener<{ type: ActionType }>) => () => void;
  addVolumeKeyEventListener: (
    listener: EventHandlerListener<{ key: InAppEventParams[InAppEventType.VolumeBtnPressed]['key'] }>,
  ) => () => void;
}

const defaultState: InAppState = {
  isPushOn: false,
  userAgent: undefined,
  addActionEventListener: () => () => {},
  addVolumeKeyEventListener: () => () => {},
};

const InAppContext = createContext<InAppState>(defaultState);

export const InAppProvider = ({ children, userAgent }: { children: ReactNode; userAgent?: string }) => {
  const [isPushOn, setIsPushOn] = useState(defaultState.isPushOn);
  const [ua, setUserAgent] = useState(userAgent);
  const [actionListeners] = useState(new EventHandler<{ type: ActionType }>());
  const [volumeKeyListeners] = useState(
    new EventHandler<{ key: InAppEventParams[InAppEventType.VolumeBtnPressed]['key'] }>(),
  );
  const { mutate: signWithSsoAccessTokenMutate } = useSignIn();
  const link = useSsoLink();

  const handleInAppEvent = useCallback(
    async (event: any) => {
      const payload = event.detail as InAppEvent<InAppEventType>;

      switch (payload.type) {
        case InAppEventType.SignWithSsoAccessToken: {
          const message = payload.message as InAppEventParams[typeof payload.type];

          if (message.isForLink) {
            const { ssoAccessToken } = message as AccessTokenType;
            await link(message.ssoType, ssoAccessToken);
          } else {
            signWithSsoAccessTokenMutate(message);
          }
          break;
        }
        case InAppEventType.ChangePushOn: {
          const message = payload.message as InAppEventParams[typeof payload.type];
          setIsPushOn(message.isPushOn);
          break;
        }
        case InAppEventType.Action: {
          const message = payload.message as InAppEventParams[typeof payload.type];
          actionListeners.emit({ type: message.type });
          break;
        }
        case InAppEventType.VolumeBtnPressed: {
          const message = payload.message as InAppEventParams[typeof payload.type];
          volumeKeyListeners.emit({ key: message.key });
          break;
        }
        default: {
          // Nothing
        }
      }
    },
    [actionListeners, link, signWithSsoAccessTokenMutate, volumeKeyListeners],
  );

  useEffect(() => {
    window.addEventListener(InAppEventName, handleInAppEvent as unknown as EventListener);
    return () => {
      window.removeEventListener(InAppEventName, handleInAppEvent as unknown as EventListener);
    };
  }, [handleInAppEvent]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setUserAgent(navigator.userAgent);
    }
  }, []);

  const value = useMemo(
    () => ({
      isPushOn,
      userAgent: ua,
      addActionEventListener: (listener: EventHandlerListener<{ type: ActionType }>) =>
        actionListeners.addEventListener(listener),
      addVolumeKeyEventListener: (
        listener: EventHandlerListener<{ key: InAppEventParams[InAppEventType.VolumeBtnPressed]['key'] }>,
      ) => volumeKeyListeners.addEventListener(listener),
    }),
    [actionListeners, isPushOn, ua, volumeKeyListeners],
  );

  return <InAppContext.Provider value={value}>{children}</InAppContext.Provider>;
};

export const useInAppState = () => {
  const context = useContext(InAppContext);

  if (!context) {
    throw new UsedOutsideProviderError('useInAppState was used outside of its Provider');
  }

  return useMemo(() => context, [context]);
};

export const useSignIn = () => {
  const { userAgent } = useInAppState();
  const client = useMemo(() => parseUserAgent(userAgent ?? ''), [userAgent]) as UseClientType;
  const router = useRouter();

  return useSignInMutation({
    onSuccess: data => {
      if (client.isWeb) {
        router.replace('/');
      } else {
        DirittoAppBridge.completeSign(data.data.token);
      }
    },
    onError: e => {
      if (axios.isAxiosError(e)) {
        const data = (e as AxiosError<{ code: AuthErrorCodes; params: string; ssoType: SsoType; ssoEmail: string }>)
          .response?.data;
        const errorCode = data?.code;

        if (errorCode === AuthErrorCodes.NeedSignUp) {
          if (data?.params) {
            router.replace(buildUrl(`/accounts/sign-up/after-sso`, { params: data.params }));
            return;
          }
        }

        if (errorCode === AuthErrorCodes.Withdrawn) {
          router.replace('/accounts/withdrawn-user');
          return;
        }

        if (errorCode === AuthErrorCodes.Dormant) {
          const dormantToken = (e as AxiosError<{ dormantToken: string }>).response?.data?.dormantToken;
          router.replace(buildUrl('/accounts/sign-in', { isDormant: 'true', dormantToken }));
          return;
        }

        if (errorCode === AuthErrorCodes.AlreadySignedUp) {
          const alreadySignedUpSsoType = data?.ssoType as SsoType;
          const ssoEmail = data?.ssoEmail;
          router.replace(
            buildUrl('/accounts/sign-up/existing-sso', {
              ssoType: alreadySignedUpSsoType,
              ssoEmail,
            }),
          );
          return;
        }
      } else {
        // Axios error 아닌 경우에만 보내줌
        SentryService.captureException(e);
      }

      showAlertModal(
        {
          title: '회원가입 오류',
          description:
            '회원가입이 정상적으로 처리되지 않았어요. 다시 시도해주세요. 문제가 지속되면 고객센터에 문의해주세요.',
        },
        { onConfirm: () => router.replace(buildSignInUrl()), confirmText: '재시도' },
      );
    },
  });
};
